import { IPersonaldaten } from './personaldaten.model';
import { IWerdegang } from './werdegang.model';
import { IAusbildung } from './ausbildung.model';
import { IProjekt } from './projekt.model';
import { IZertifikat } from './zertifikat.model';
import { ISchulung } from './schulung.model';
import { IFaehigkeit } from './faehigkeit.model';
import { IBerater } from './berater.model';

export type CvBlockType = 'HEADING' | 'PARAGRAPH' | 'TABLE';

export interface CvBlock {
  type?: CvBlockType;
  level?: number;
  text?: string;
}

/** Result of `POST api/berater/{id}/cv-extract` (raw .docx text + structure hints). */
export interface CvExtractionResult {
  text?: string;
  blocks?: CvBlock[];
}

/**
 * Die einzigen Sprachen, die `cv-structure` annimmt (NP20-990); jeder andere Wert
 * wird mit 422 quittiert. Steuert im Backend zugleich das Anonymisierungs-Modell
 * und die Sprache, in der die KI die Freitext-Felder schreibt.
 */
export type CvImportLanguage = 'de' | 'en';

/** Vorbelegung im Assistenten — deckungsgleich mit dem Backend-Default (NP20-1071). */
export const DEFAULT_CV_IMPORT_LANGUAGE: CvImportLanguage = 'en';

/** Body of `POST api/berater/{id}/cv-structure`. */
export interface CvStructureRequest {
  text: string;
  language?: CvImportLanguage;
}

/** A single proposed value (partial section model) with the model's confidence and source snippet. */
export interface CvDraftEntry<T> {
  value?: Partial<T>;
  confidence?: number;
  source?: string;
}

/** Result of `POST api/berater/{id}/cv-structure`: the structured profile draft. */
export interface CvProfileDraft {
  personaldaten?: CvDraftEntry<IPersonaldaten>;
  werdegang?: CvDraftEntry<IWerdegang>[];
  ausbildung?: CvDraftEntry<IAusbildung>[];
  projekte?: CvDraftEntry<IProjekt>[];
  zertifikate?: CvDraftEntry<IZertifikat>[];
  schulungen?: CvDraftEntry<ISchulung>[];
  faehigkeiten?: CvDraftEntry<IFaehigkeit>[];
}

/** Body of `POST api/berater/{id}/cv-apply` — the approved sections, unwrapped and appended non-destructively. */
export interface CvApplyRequest {
  personaldaten?: Partial<IPersonaldaten>;
  werdegang?: Partial<IWerdegang>[];
  ausbildung?: Partial<IAusbildung>[];
  projekte?: Partial<IProjekt>[];
  zertifikate?: Partial<IZertifikat>[];
  schulungen?: Partial<ISchulung>[];
  faehigkeiten?: Partial<IFaehigkeit>[];
}

/**
 * Result of `POST api/berater/{id}/cv-apply` (NP20-874/875): the updated profile
 * plus what the lenient import silently dropped, so the user sees it instead of
 * a blank success. `needsReview` flags a persisted-but-inconsistent value (e.g.
 * restricted travel without a reason, NP20-838) that the user must still fix.
 */
export interface CvApplyResult {
  profile: IBerater;
  skippedSkills: string[];
  skippedBranches: string[];
  skippedSchwerpunkte: string[];
  skippedDuplicates: number;
  needsReview: boolean;
}
